/**
 * @file   src\containers\Manager\Report\CallOutReplacementReport.tsx
 * @brief  Call Out -Replacement Report page
 * @date   June, 2025
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { Row, Col, Table, Button, useState, useEffect, moment, Dropdown } from '../../../components/ThirdPartyComponents';
import Strings from '../../../assests/strings/Strings.json';
import PDFIcon from '../../../assests/icons/PDF';
import Sort from '../../../assests/icons/Sort';
import KKIDatepicker from '../../../components/KKIDatepicker';
import EXPPDF from '../../../assests/icons/PDFIcon';
import EXPExcel from '../../../assests/icons/Excel';

import {
  DATE_END,
  DATE_FORMAT_HHMMA,
  DATE_FORMAT_MM_DD_YYYY_DDD,
  DATE_FORMAT_MM_DD_YYYY_FILTER,
  DATE_FORMAT_MM_DD_YYYY_HMMA_DDD,
  DATE_FORMAT_YYYY_MM_DD,
  DATE_START,
  HTTP_STATUS_200,
  MONTHS_LABEL,
  NIGHT_SHIFTS,
  NUMBER_0,
  NUMBER_1,
  NUMBER_3,
  NUMBER_50,
  PAGE_SIZE,
} from '../../../utils/constants';
import KKISelect from '../../../components/KKISelect';
import KKIInput from '../../../components/KKIInput';
import { IObject } from '../../../interfaces/generalInterface';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { RootState } from '../../../store';
import { getCalloutReasons, revertAllCommonState } from '../../../store/actions/commonAction';
import { getAllShiftTypes, revertAllScheduleState } from '../../../store/actions/manageCAScheduleAction';
import { ICalloutReasons } from '../../../interfaces/commonInterface';
import { MessageToaster } from '../../../utils/toastUtils';
import { IShiftTypes } from '../../../interfaces/scheduleInterface';
import { getDateDifference, stringFormat } from '../../../utils/helper';
import { ICalloutPdfReportRequest, ICalloutReportList, ICalloutReportRequest } from '../../../interfaces/reportInterface';
import { CalloutReportSortField, SortOrderType } from '../../../utils/enums';
import { exportCalloutReportAsExcel, exportCalloutReportAsPdf, getCalloutReport, revertAllReportState } from '../../../store/actions/reportAction';
import SortDownIcon from '../../../assests/icons/SortDown';
import SortUpIcon from '../../../assests/icons/SortUp';
import Loader from '../../../components/Loader';
import Pagination from '../../../components/Paginate';
import { DATE_RANGE_VALIDATION_MESSAGE } from '../../../messages/validationMessages';

// Toast object creation.
const toast = new MessageToaster();
// Define item count to be displayed per page.
const pageItemCount: number = PAGE_SIZE;

const CallOutReplacement = () => {
  // Declare action dispatch.
  const dispatch = useAppDispatch();

  // Access redux state variables.
  const { isFetchCalloutReasonSuccess, fetchCalloutReasonErrorCode, fetchCalloutReasonMessage, callOutReasons } = useAppSelector((state: RootState) => state.common);
  const { isShiftTypesSuccess, shiftTypesErrorCode, shiftTypesMessage, shiftTypes } = useAppSelector((state: RootState) => state.manageCASchedule);
  const {
    isCalloutReportLoading,
    isCalloutReportSuccess,
    calloutReportErrorCode,
    calloutReportMessage,
    calloutReportData,
    totalCallout,
    isExportCalloutPdfLoading,
    isExportCalloutPdfSuccess,
    exportCalloutPdfErrorCode,
    exportCalloutPdfMessage,
    callOutPdfData,
    isExportCalloutExcelLoading,
    isExportCalloutExcelSuccess,
    exportCalloutExcelErrorCode,
    exportCalloutExcelMessage,
    calloutExcelData,
  } = useAppSelector((state: RootState) => state.report);

  // Initialize component state variables.
  const [shiftTypeList, setShiftTypeList] = useState<IObject[]>([]);
  const [selectedShift, setSelectedShift] = useState<IObject | null>(null);
  const [callOutReasonList, setCallOutReasonList] = useState<IObject[]>([]);
  const [selectedReason, setSelectedReason] = useState<IObject | null>(null);
  const [caName, setCaName] = useState<string>('');
  const [startDateSelected, setStartDateSelected] = useState<Date | null>(moment().toDate());
  const [endDateSelected, setEndDateSelected] = useState<Date | null>(moment().toDate());
  const [pageSize] = useState<number>(pageItemCount);
  const [pageIndex, setPageIndex] = useState<number>(NUMBER_1);
  const [sortBy, setSortBy] = useState<string>(CalloutReportSortField[CalloutReportSortField.shiftStartDate]);
  const [sortOrder, setSortOrder] = useState<number>(SortOrderType.ASC);

  // Method to detch callout api data.
  const fetchCallOutSummary = () => {
    let fromDate = '';
    let toDate = '';
    if (startDateSelected && endDateSelected) {
      fromDate = moment(new Date(startDateSelected)).format(DATE_FORMAT_YYYY_MM_DD);
      toDate = moment(new Date(endDateSelected)).format(DATE_FORMAT_YYYY_MM_DD);
    }
    const reportRequest: ICalloutReportRequest = {
      shiftCode: selectedShift ? Number(selectedShift.value) : NUMBER_0,
      caName,
      reasonType: selectedReason ? Number(selectedReason.value) : NUMBER_0,
      fromDate,
      toDate,
      page: pageIndex,
      limit: pageSize,
      order: SortOrderType[sortOrder],
      sort: sortBy,
    };
    dispatch(getCalloutReport(reportRequest));
  };

  // Handle component side effect and fetch all shift types/callout reasons.
  useEffect(() => {
    dispatch(getAllShiftTypes());
    dispatch(getCalloutReasons());
    return () => {
      dispatch(revertAllCommonState());
      dispatch(revertAllReportState());
      dispatch(revertAllScheduleState());
    };
  }, []);

  // Fetch callout report based on state change.
  useEffect(() => {
    fetchCallOutSummary();
  }, [pageIndex, sortBy, sortOrder]);

  // Shift type response state change.
  useEffect(() => {
    if (isShiftTypesSuccess && shiftTypesErrorCode === HTTP_STATUS_200 && shiftTypes) {
      const shiftTypeOptions = shiftTypes.map((shift: IShiftTypes): IObject => ({ label: `${shift.shiftName} (${shift.shiftShortName})`, value: shift.shiftId.toString() }));
      shiftTypeOptions.unshift({ label: 'All', value: NUMBER_0.toString() });
      setShiftTypeList(shiftTypeOptions);
      setSelectedShift(shiftTypeOptions[0] ?? null);
    } else if (!isShiftTypesSuccess && shiftTypesErrorCode > HTTP_STATUS_200 && shiftTypesMessage) {
      toast.toastError(shiftTypesMessage);
    }
  }, [isShiftTypesSuccess, shiftTypesErrorCode, shiftTypes, shiftTypesMessage]);

  // Callout reasons api response state change handling.
  useEffect(() => {
    if (isFetchCalloutReasonSuccess && fetchCalloutReasonErrorCode === HTTP_STATUS_200) {
      if (callOutReasons && callOutReasons.length > 0) {
        const reasonOptions = callOutReasons.map((cout: ICalloutReasons): IObject => ({ label: cout.reason, value: cout.reasonId.toString() }));
        reasonOptions.unshift({ label: 'All', value: NUMBER_0.toString() });
        setCallOutReasonList(reasonOptions);
        setSelectedReason(reasonOptions[0] ?? null);
      }
    } else if (!isFetchCalloutReasonSuccess && fetchCalloutReasonErrorCode > HTTP_STATUS_200 && fetchCalloutReasonMessage) {
      toast.toastError(fetchCalloutReasonMessage);
      dispatch(revertAllCommonState());
    }
  }, [isFetchCalloutReasonSuccess, fetchCalloutReasonErrorCode, fetchCalloutReasonMessage, callOutReasons]);

  // Callout report fetch api response state change.
  useEffect(() => {
    if (!isCalloutReportSuccess && calloutReportErrorCode > HTTP_STATUS_200 && calloutReportMessage) {
      toast.toastError(calloutReportMessage);
    }
  }, [isCalloutReportSuccess, calloutReportErrorCode, calloutReportMessage]);

  // Export pdf api response state change handling.
  useEffect(() => {
    if (isExportCalloutPdfSuccess && callOutPdfData) {
      const pdfBlob = new Blob([callOutPdfData], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // Create a link element, set download attributes, and trigger a click
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.setAttribute('download', 'CalloutReport.pdf'); // Set the PDF filename
      document.body.appendChild(link);
      link.click();
      toast.toastSuccess(Strings.Text.CalloutExportSuccessText);
      // Cleanup: remove the link and revoke the URL
      document.body.removeChild(link);
      URL.revokeObjectURL(pdfUrl);
      dispatch(revertAllReportState());
    } else if (!isExportCalloutPdfSuccess && exportCalloutPdfErrorCode > HTTP_STATUS_200 && exportCalloutPdfMessage) {
      toast.toastError(exportCalloutPdfMessage);
      dispatch(revertAllReportState());
    }
  }, [isExportCalloutPdfSuccess, exportCalloutPdfErrorCode, exportCalloutPdfMessage, callOutPdfData]);

  // Export excel api response state change handling.
  useEffect(() => {
    if (isExportCalloutExcelSuccess && calloutExcelData) {
      const excelBlob = new Blob([calloutExcelData], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const excelUrl = URL.createObjectURL(excelBlob);

      // Create a link element, set download attributes, and trigger a click
      const link = document.createElement('a');
      link.href = excelUrl;
      link.setAttribute('download', 'CalloutReport.xlsx'); // Set the Excel filename
      document.body.appendChild(link);
      link.click();
      toast.toastSuccess(Strings.Text.CalloutExportSuccessText);
      // Cleanup: remove the link and revoke the URL
      document.body.removeChild(link);
      URL.revokeObjectURL(excelUrl);
      dispatch(revertAllReportState());
    } else if (!isExportCalloutExcelSuccess && exportCalloutExcelErrorCode > HTTP_STATUS_200 && exportCalloutExcelMessage) {
      toast.toastError(exportCalloutExcelMessage);
      dispatch(revertAllReportState());
    }
  }, [isExportCalloutExcelSuccess, exportCalloutExcelErrorCode, exportCalloutExcelMessage, calloutExcelData]);

  // Callout reason type dropdown select change handler.
  const onCalloutReasonSelect = (val: IObject) => {
    setSelectedReason(val);
  };

  // Shift type select change handler.
  const onShiftTypeSelect = (val: IObject) => {
    setSelectedShift(val);
  };

  // CA Name input change handler.
  const onCaNameChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target;
    setCaName(value);
  };

  // Start/end date input select event handler.
  const handleDateChange = (date: Date | null, type: string) => {
    let days = NUMBER_1;
    if (type === DATE_START) {
      days = getDateDifference(moment(date).format(DATE_FORMAT_YYYY_MM_DD), moment(endDateSelected).format(DATE_FORMAT_YYYY_MM_DD));
      if (days < 0) {
        setStartDateSelected(date);
        setEndDateSelected(date);
      } else {
        setStartDateSelected(date);
      }
    } else {
      days = getDateDifference(moment(startDateSelected).format(DATE_FORMAT_YYYY_MM_DD), moment(date).format(DATE_FORMAT_YYYY_MM_DD));
      setEndDateSelected(date);
    }
  };

  // Generate report button click handler.
  const onGenerateReport = () => {
    if (moment(endDateSelected).diff(moment(startDateSelected), MONTHS_LABEL, true) > NUMBER_3) {
      toast.toastError(DATE_RANGE_VALIDATION_MESSAGE);
      return;
    }
    setSortBy(CalloutReportSortField[CalloutReportSortField.shiftStartDate]);
    setSortOrder(SortOrderType.ASC);
    if (pageIndex !== NUMBER_1) {
      setPageIndex(NUMBER_1);
    } else {
      fetchCallOutSummary();
    }
  };

  // Sort header change event.
  const handleSort = (field: string) => {
    let newSortOrder = SortOrderType.ASC;
    if (field === sortBy) {
      newSortOrder = sortOrder === SortOrderType.ASC ? SortOrderType.DESC : SortOrderType.ASC;
    }
    setSortBy(field);
    setSortOrder(newSortOrder);
  };

  // Export callout request binding.
  const getCalloutReportExportRequest = (): ICalloutPdfReportRequest => {
    let fromDate = '';
    let toDate = '';
    if (startDateSelected && endDateSelected) {
      if (startDateSelected && endDateSelected) {
        fromDate = moment(new Date(startDateSelected)).format(DATE_FORMAT_YYYY_MM_DD);
        toDate = moment(new Date(endDateSelected)).format(DATE_FORMAT_YYYY_MM_DD);
      }
    }
    const reportRequest: ICalloutPdfReportRequest = {
      shiftCode: selectedShift ? Number(selectedShift.value) : NUMBER_0,
      caName,
      reasonType: selectedReason ? Number(selectedReason.value) : NUMBER_0,
      fromDate,
      toDate,
      page: NUMBER_0,
      order: SortOrderType[sortOrder],
      sort: sortBy,
    };
    return reportRequest;
  };

  // Handle the "Export as PDF" button click event.
  const handleExportPDFClick = (): void => {
    const reportRequest = getCalloutReportExportRequest();
    dispatch(exportCalloutReportAsPdf(reportRequest));
  };

  // Handle the "Export as Excel" button click event.
  const handleExportExcelClick = (): void => {
    const reportRequest = getCalloutReportExportRequest();
    dispatch(exportCalloutReportAsExcel(reportRequest));
  };

  return (
    <div>
      <div className="pageheader">
        <Row className="align-items-center justify-content-between">
          <Col>
            <h1>{Strings.HD.CallOutReplacement}</h1>
          </Col>
        </Row>
      </div>
      <div className="filter-main mb-3">
        <Row className="d-flex justify-content-between">
          <Col xl={7} md={12} sm={12} className="mb-sm-3 mb-lg-0 calloutreport-filter">
            <Col>
              <Row className="align-items-center">
                <Col xs="auto" className="pe-0">
                  {Strings.Label.ShiftCode}:
                </Col>
                <Col xs>
                  <KKISelect
                    id="shiftCode"
                    name="shiftCode"
                    className="custom-select"
                    placeholder={Strings.Label.ShiftCode}
                    searchvalue={false}
                    options={shiftTypeList}
                    value={selectedShift}
                    onSelectChange={onShiftTypeSelect}
                  />
                </Col>
                <Col xs="auto" className="ps-3 pe-0">
                  {Strings.Schedule.DateFrom}
                </Col>
                <Col xs className="calendar-mob-left">
                  <KKIDatepicker
                    id="startDate"
                    name="startDate"
                    placeholder={Strings.Label.StartDate}
                    value={startDateSelected}
                    dateFormat={DATE_FORMAT_MM_DD_YYYY_FILTER}
                    onChangeDatepicker={(date: any) => handleDateChange(date, DATE_START)}
                  />
                </Col>
                <Col xs="auto" className="px-0">
                  {Strings.Schedule.DateFilterText}
                </Col>
                <Col xs className="calendar-mob-right">
                  <KKIDatepicker
                    id="endDate"
                    name="endDate"
                    placeholder={Strings.Label.EndDate}
                    value={endDateSelected}
                    dateFormat={DATE_FORMAT_MM_DD_YYYY_FILTER}
                    minDate={startDateSelected || moment().toDate()}
                    onChangeDatepicker={(date: any) => handleDateChange(date, DATE_END)}
                  />
                </Col>
              </Row>
            </Col>
            <Col className="mt-2">
              <Row className="align-items-center">
                <Col xs="auto" className="pe-0 ps-20">
                  {Strings.Label.CAName}:
                </Col>
                <Col xs>
                  <KKIInput id="caName" name="caName" type="text" placeholder={Strings.Label.CANamePlaceHolder} value={caName} maxLength={NUMBER_50} onChange={onCaNameChange} />
                </Col>
                <Col xs="auto" className="pe-0">
                  {Strings.Label.CallOutReasonType}:
                </Col>
                <Col xs>
                  <KKISelect
                    id="status"
                    name="status"
                    className="custom-select"
                    placeholder={Strings.Label.Status}
                    searchvalue={false}
                    options={callOutReasonList}
                    value={selectedReason}
                    onSelectChange={onCalloutReasonSelect}
                  />
                </Col>
                <Col md="auto" className="ps-0">
                  <Button variant="primary" type="button" onClick={() => onGenerateReport()}>
                    {Strings.Button.GenerateReport}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Col>
          <Col md="auto" className="d-flex align-items-center ">
            <Dropdown className="exportfile">
              <Dropdown.Toggle variant="outline-primary" id="dropdown-basic">
                <PDFIcon />
                {Strings.Button.Export}
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item href={void 0} onClick={handleExportPDFClick}>
                  <EXPPDF />
                  {Strings.Button.PDF}
                </Dropdown.Item>
                <Dropdown.Item href={void 0} onClick={handleExportExcelClick}>
                  <EXPExcel />
                  {Strings.Button.Excel}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </Row>
      </div>

      <div className="table-border">
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th onClick={() => handleSort(CalloutReportSortField[CalloutReportSortField.shiftCode])} style={{ cursor: 'pointer' }}>
                {Strings.Label.ShiftCode}
                <span className="ms-1">
                  {sortBy !== CalloutReportSortField[CalloutReportSortField.shiftCode] && <Sort />}
                  {sortBy === CalloutReportSortField[CalloutReportSortField.shiftCode] && sortOrder === SortOrderType.DESC && <SortDownIcon />}
                  {sortBy === CalloutReportSortField[CalloutReportSortField.shiftCode] && sortOrder === SortOrderType.ASC && <SortUpIcon />}
                </span>
              </th>
              <th onClick={() => handleSort(CalloutReportSortField[CalloutReportSortField.shiftStartDate])} style={{ cursor: 'pointer' }}>
                {Strings.Label.ShiftDate}
                <span className="ms-1">
                  {sortBy !== CalloutReportSortField[CalloutReportSortField.shiftStartDate] && <Sort />}
                  {sortBy === CalloutReportSortField[CalloutReportSortField.shiftStartDate] && sortOrder === SortOrderType.DESC && <SortDownIcon />}
                  {sortBy === CalloutReportSortField[CalloutReportSortField.shiftStartDate] && sortOrder === SortOrderType.ASC && <SortUpIcon />}
                </span>
              </th>
              <th onClick={() => handleSort(CalloutReportSortField[CalloutReportSortField.caName])} style={{ cursor: 'pointer' }}>
                {Strings.Label.CalledOutCAName}
                <span className="ms-1">
                  {sortBy !== CalloutReportSortField[CalloutReportSortField.caName] && <Sort />}
                  {sortBy === CalloutReportSortField[CalloutReportSortField.caName] && sortOrder === SortOrderType.DESC && <SortDownIcon />}
                  {sortBy === CalloutReportSortField[CalloutReportSortField.caName] && sortOrder === SortOrderType.ASC && <SortUpIcon />}
                </span>
              </th>
              <th onClick={() => handleSort(CalloutReportSortField[CalloutReportSortField.callOutDateTime])} style={{ cursor: 'pointer' }}>
                {Strings.Label.CallOutDateTime}
                <span className="ms-1">
                  {sortBy !== CalloutReportSortField[CalloutReportSortField.callOutDateTime] && <Sort />}
                  {sortBy === CalloutReportSortField[CalloutReportSortField.callOutDateTime] && sortOrder === SortOrderType.DESC && <SortDownIcon />}
                  {sortBy === CalloutReportSortField[CalloutReportSortField.callOutDateTime] && sortOrder === SortOrderType.ASC && <SortUpIcon />}
                </span>
              </th>
              <th>{Strings.Label.CallOutReasonType}</th>
              <th>{Strings.Label.ReasonDetails}</th>
              <th>{Strings.Label.ReplacementCAName}</th>
            </tr>
          </thead>
          <tbody>
            {calloutReportData &&
              calloutReportData.length > NUMBER_0 &&
              calloutReportData.map((att: ICalloutReportList, index: number) => (
                <tr key={`${att.shiftId}-${att.caId}-${index}`}>
                  <td>{`${att.shiftName} (${att.shiftCode})`}</td>
                  <td>
                    {NIGHT_SHIFTS.includes(att.shiftCode) ? (
                      <>
                        {moment.utc(att.shiftStartDate).format(DATE_FORMAT_MM_DD_YYYY_HMMA_DDD)} -
                        <span className="d-block"> {moment.utc(att.shiftEndDate).format(DATE_FORMAT_MM_DD_YYYY_HMMA_DDD)}</span>
                      </>
                    ) : (
                      `${moment.utc(att.shiftStartDate).format(DATE_FORMAT_MM_DD_YYYY_DDD)}, `
                    )}
                    {!NIGHT_SHIFTS.includes(att.shiftCode) ? (
                      <span className="shift-time-web">
                        {moment.utc(att.shiftStartDate).format(DATE_FORMAT_HHMMA)} - {moment.utc(att.shiftEndDate).format(DATE_FORMAT_HHMMA)}
                      </span>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>{att.caName || '-'}</td>
                  <td>{moment.utc(att.callOutDateTime).format(DATE_FORMAT_MM_DD_YYYY_HMMA_DDD)}</td>
                  <td>{att.callOutReason || '-'}</td>
                  <td>{att.callOutReasonDetails || '-'}</td>
                  <td>{att.replacedCaName || '-'}</td>
                </tr>
              ))}
            {!calloutReportData ||
              (calloutReportData && calloutReportData.length === NUMBER_0 && (
                <tr>
                  <td colSpan={7} className="text-center">
                    {Strings.Text.NoDataFound}
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </div>
      <div className="d-flex justify-content-between mt-4">
        <span>
          {(() => {
            const displayText = '';
            if (calloutReportData && calloutReportData.length > NUMBER_0 && totalCallout > 0) {
              const callOut = totalCallout === NUMBER_1 ? NUMBER_1 : pageIndex;
              const start = (callOut - NUMBER_1) * pageSize + NUMBER_1;
              const end = (callOut - NUMBER_1) * pageSize + calloutReportData.length;
              return stringFormat(Strings.Text.CalloutReportPaginationText, start, end, totalCallout);
            }
            return displayText;
          })()}
        </span>
        {calloutReportData && calloutReportData.length > NUMBER_0 && totalCallout > pageSize && (
          <Pagination totalCount={totalCallout} pageLimit={pageSize} setCurrentPage={(page: number) => setPageIndex(page)} currentPage={pageIndex - NUMBER_1} prevPage={-1} />
        )}
      </div>
      {(isCalloutReportLoading || isExportCalloutPdfLoading || isExportCalloutExcelLoading) && <Loader />}
    </div>
  );
};
export default CallOutReplacement;
